import { Badge, Button, Center, Code, Collapse, Group, Paper, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconDatabase } from '@tabler/icons-react';
import { useState } from 'react';

import type { Change } from '@admin/hooks';

interface DatabaseChangesTabProps {
  dbChanges: Change[];
}

export const DatabaseChangesTab = ({ dbChanges }: DatabaseChangesTabProps) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const getOperationColor = (operation: string) => {
    switch (operation?.toLowerCase()) {
      case 'insert':
        return 'green';
      case 'update':
        return 'blue';
      case 'delete':
        return 'red';
      case 'replace':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (dbChanges.length === 0) {
    return (
      <Center p="xl">
        <Text c="dimmed">No database changes were made during this request</Text>
      </Center>
    );
  }

  return (
    <Stack gap="xs">
      {dbChanges.map((change) => (
        <Paper key={change._id} withBorder p="md">
          <Group justify="space-between" mb="xs">
            <Group>
              <ThemeIcon color={getOperationColor(change.operationType)} variant="light" size="md">
                <IconDatabase size={16} />
              </ThemeIcon>
              <div>
                <Text size="sm" fw={500}>
                  {change.collectionName}
                </Text>
                <Text size="xs" c="dimmed">
                  Document ID: {change.documentId}
                </Text>
              </div>
            </Group>
            <Badge color={getOperationColor(change.operationType)}>{change.operationType}</Badge>
          </Group>

          <Collapse in={expandedId === change._id}>
            <Stack gap="md" mt="md">
              {change.changes && Object.keys(change.changes).length > 0 && (
                <div>
                  <Text size="xs" c="dimmed" mb={4}>
                    Changed Fields:
                  </Text>
                  <Code block style={{ maxHeight: '200px', overflow: 'auto' }}>
                    {JSON.stringify(change.changes, null, 2)}
                  </Code>
                </div>
              )}
            </Stack>
          </Collapse>

          <Button variant="subtle" size="xs" onClick={() => toggleExpand(change._id)} mt="xs">
            {expandedId === change._id ? 'Hide' : 'Show'} Details
          </Button>
        </Paper>
      ))}
    </Stack>
  );
};
